import React, { useEffect, useState } from 'react';

function KotPricingProfileMaster({ token, selectedProperty, selectedOutletId = '' }) {
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState(selectedOutletId || '');
  const [taxProfiles, setTaxProfiles] = useState([]);
  const [pricingProfiles, setPricingProfiles] = useState([]);
  const [selectedPricingProfileId, setSelectedPricingProfileId] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [itemPricingRows, setItemPricingRows] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [pricingForm, setPricingForm] = useState({
    name: '',
    description: '',
    is_default: false,
    is_active: true,
    valid_from: '',
    valid_to: ''
  });
  const [itemPricingForm, setItemPricingForm] = useState({
    menu_item_id: '',
    start_time: '',
    end_time: '',
    price: '',
    is_active: true
  });
  const [defaultTaxProfileId, setDefaultTaxProfileId] = useState('');
  const [defaultPricingProfileId, setDefaultPricingProfileId] = useState('');
  const [savedPricingProfileId, setSavedPricingProfileId] = useState(null);

  const chainId = selectedProperty?.chain_id ? String(selectedProperty.chain_id).trim() : '';
  const propertyId = selectedProperty?.id ? String(selectedProperty.id).trim() : '';
  const baseHeaders = { Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId };
  const scopedHeaders = (extra = {}) => ({ ...baseHeaders, 'x-outlet-id': outletId, ...extra });

  const clearStatus = () => {
    setError('');
    setMessage('');
  };

  const loadOutlets = async () => {
    if (!chainId || !propertyId) return;
    try {
      const res = await fetch(`/api/v1/core/outlets?property_id=${encodeURIComponent(propertyId)}`, { headers: baseHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load outlets');
      const all = Array.isArray(data) ? data : [];
      const rows = all.filter(o => String(o.outlet_type || '').toUpperCase() !== 'BAR' || o.module_kot_enabled === true);
      setOutlets(rows);
      const matchSelected = selectedOutletId && rows.find((r) => String(r.id) === String(selectedOutletId));
      const selected = matchSelected || rows.find((r) => String(r.id) === String(outletId)) || rows[0];
      setOutletId(selected?.id || '');
      setDefaultTaxProfileId(selected?.default_tax_profile_id || '');
      setDefaultPricingProfileId(selected?.default_pricing_profile_id || '');
      setSavedPricingProfileId(selected?.default_pricing_profile_id || null);
    } catch (err) {
      setOutlets([]);
      setOutletId('');
      setError(err?.message || 'Network error while loading outlets');
    }
  };

  const loadProfiles = async () => {
    if (!outletId) return;
    try {
      const [pricingRes, taxRes, itemsRes] = await Promise.all([
        fetch('/api/v1/kot/masters/pricing-profiles', { headers: scopedHeaders() }),
        fetch('/api/v1/kot/masters/tax-profiles', { headers: scopedHeaders() }),
        fetch('/api/v1/kot/masters/menu-items', { headers: scopedHeaders() })
      ]);
      const pricingData = await pricingRes.json().catch(() => ({}));
      const taxData = await taxRes.json().catch(() => ({}));
      const itemData = await itemsRes.json().catch(() => ({}));
      if (!pricingRes.ok) throw new Error(pricingData.error || 'Failed to load pricing profiles');
      if (!taxRes.ok) throw new Error(taxData.error || 'Failed to load tax profiles');
      if (!itemsRes.ok) throw new Error(itemData.error || 'Failed to load menu items');
      const pricingRows = Array.isArray(pricingData) ? pricingData : [];
      const taxRows = Array.isArray(taxData) ? taxData : [];
      const menuRows = Array.isArray(itemData) ? itemData : [];
      setPricingProfiles(pricingRows);
      setTaxProfiles(taxRows);
      setMenuItems(menuRows);
      const selectedPricing = pricingRows.find((r) => String(r.id) === String(selectedPricingProfileId)) || pricingRows[0];
      setSelectedPricingProfileId(selectedPricing?.id || '');
      if (!itemPricingForm.menu_item_id && menuRows[0]?.id) {
        setItemPricingForm((prev) => ({ ...prev, menu_item_id: menuRows[0].id }));
      }
      const selectedOutlet = outlets.find((r) => String(r.id) === String(outletId));
      setDefaultTaxProfileId(selectedOutlet?.default_tax_profile_id || '');
      setDefaultPricingProfileId(selectedOutlet?.default_pricing_profile_id || '');
      setSavedPricingProfileId(selectedOutlet?.default_pricing_profile_id || null);
    } catch (err) {
      setError(err?.message || 'Network error while loading profile masters');
      setPricingProfiles([]);
      setTaxProfiles([]);
      setMenuItems([]);
      setSelectedPricingProfileId('');
      setItemPricingRows([]);
    }
  };

  const loadItemPricing = async () => {
    if (!outletId || !selectedPricingProfileId) return setItemPricingRows([]);
    try {
      const res = await fetch(`/api/v1/kot/masters/pricing-profiles/${selectedPricingProfileId}/item-pricing`, { headers: scopedHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load item pricing');
      setItemPricingRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Network error while loading item pricing');
      setItemPricingRows([]);
    }
  };

  useEffect(() => {
    setOutlets([]);
    setOutletId(selectedOutletId || '');
    setPricingProfiles([]);
    setTaxProfiles([]);
    setMenuItems([]);
    setSelectedPricingProfileId('');
    setItemPricingRows([]);
    if (chainId && propertyId) loadOutlets();
  }, [chainId, propertyId, token]);

  useEffect(() => {
    if (selectedOutletId && outlets.some((o) => String(o.id) === String(selectedOutletId))) {
      setOutletId(selectedOutletId);
    }
  }, [selectedOutletId]);

  useEffect(() => {
    if (!outletId) return;
    loadProfiles();
  }, [outletId]);

  useEffect(() => {
    loadItemPricing();
  }, [selectedPricingProfileId]);

  const createPricingProfile = async () => {
    clearStatus();
    if (!outletId) return setError('Select outlet first');
    if (!String(pricingForm.name || '').trim()) return setError('Profile name is required');
    try {
      const payload = {
        ...pricingForm,
        name: String(pricingForm.name || '').trim(),
        description: String(pricingForm.description || '').trim() || null,
        valid_from: pricingForm.valid_from || null,
        valid_to: pricingForm.valid_to || null
      };
      const res = await fetch('/api/v1/kot/masters/pricing-profiles', {
        method: 'POST',
        headers: scopedHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to create pricing profile');
      setPricingForm({ name: '', description: '', is_default: false, is_active: true, valid_from: '', valid_to: '' });
      setMessage('Pricing profile created');
      await loadProfiles();
      setSelectedPricingProfileId(data.id);
    } catch (err) {
      setError(err?.message || 'Network error');
    }
  };

  const addItemPricing = async () => {
    clearStatus();
    if (!selectedPricingProfileId) return setError('Select pricing profile first');
    if (!itemPricingForm.menu_item_id) return setError('Select menu item');
    if (itemPricingForm.price === '' || Number.isNaN(Number(itemPricingForm.price))) return setError('Valid price is required');
    try {
      const payload = {
        ...itemPricingForm,
        price: Number(itemPricingForm.price),
        start_time: itemPricingForm.start_time || null,
        end_time: itemPricingForm.end_time || null
      };
      const res = await fetch(`/api/v1/kot/masters/pricing-profiles/${selectedPricingProfileId}/item-pricing`, {
        method: 'POST',
        headers: scopedHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to add item pricing');
      setItemPricingForm((prev) => ({ ...prev, start_time: '', end_time: '', price: '', is_active: true }));
      setMessage('Item pricing added');
      loadItemPricing();
    } catch (err) {
      setError(err?.message || 'Network error');
    }
  };

  const saveOutletDefaults = async () => {
    clearStatus();
    if (!outletId) return setError('Select outlet first');
    try {
      const res = await fetch('/api/v1/kot/masters/outlet/default-profiles', {
        method: 'PUT',
        headers: scopedHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          tax_profile_id: defaultTaxProfileId || null,
          pricing_profile_id: defaultPricingProfileId || null
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save outlet defaults');
      setSavedPricingProfileId(defaultPricingProfileId || null);
      setMessage('Outlet default profiles updated');
      loadOutlets();
    } catch (err) {
      setError(err?.message || 'Network error');
    }
  };

  const inputStyle = { padding: '0.65rem', border: '1px solid #dcdde1', borderRadius: '6px' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>KOT Pricing Profile Master</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div style={{ color: '#2f3640', marginBottom: '0.7rem' }}>{message}</div>}

      <div className="filter-bar" style={{ marginBottom: '1rem' }}>
        <h3>Scope</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.7rem' }}>
          <input readOnly value={selectedProperty ? `${selectedProperty.code || ''} - ${selectedProperty.name || ''}` : 'No property selected'} style={{ ...inputStyle, background: '#ecf0f1' }} />
          <select value={outletId} onChange={(e) => setOutletId(e.target.value)} style={inputStyle}>
            <option value="">Select Outlet</option>
            {outlets.map((o) => <option key={o.id} value={o.id}>{o.code} - {o.name}</option>)}
          </select>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: '1rem' }}>
        <h3>Outlet Defaults</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.7rem' }}>
          <select value={defaultTaxProfileId} onChange={(e) => setDefaultTaxProfileId(e.target.value)} style={inputStyle}>
            <option value="">No Tax Profile</option>
            {taxProfiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={defaultPricingProfileId} onChange={(e) => setDefaultPricingProfileId(e.target.value)} style={inputStyle}>
            <option value="">No Pricing Profile</option>
            {pricingProfiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <button onClick={saveOutletDefaults} className="btn-primary" style={{ border: 'none', borderRadius: '6px', padding: '0.6rem 1rem', cursor: 'pointer' }}>Save Outlet Default Profiles</button>
          <span style={{ fontSize: '0.85rem', color: savedPricingProfileId ? '#27ae60' : '#e74c3c', fontWeight: 600 }}>
            {savedPricingProfileId
              ? `✓ Saved: ${pricingProfiles.find(p => String(p.id) === String(savedPricingProfileId))?.name || 'Unknown'}`
              : '⚠ No default pricing profile saved — pricing rows will not apply'}
          </span>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: '1rem' }}>
        <h3>Create Pricing Profile</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem' }}>
          <input placeholder="Profile Name" value={pricingForm.name} onChange={(e) => setPricingForm({ ...pricingForm, name: e.target.value })} style={inputStyle} />
          <input placeholder="Description" value={pricingForm.description} onChange={(e) => setPricingForm({ ...pricingForm, description: e.target.value })} style={inputStyle} />
          <input type="date" value={pricingForm.valid_from} onChange={(e) => setPricingForm({ ...pricingForm, valid_from: e.target.value })} style={inputStyle} />
          <input type="date" value={pricingForm.valid_to} onChange={(e) => setPricingForm({ ...pricingForm, valid_to: e.target.value })} style={inputStyle} />
          <label><input type="checkbox" checked={pricingForm.is_default} onChange={(e) => setPricingForm({ ...pricingForm, is_default: e.target.checked })} /> Default</label>
          <label><input type="checkbox" checked={pricingForm.is_active} onChange={(e) => setPricingForm({ ...pricingForm, is_active: e.target.checked })} /> Active</label>
        </div>
        <div style={{ marginTop: '0.75rem' }}>
          <button onClick={createPricingProfile} className="btn-primary" style={{ border: 'none', borderRadius: '6px', padding: '0.6rem 1rem', cursor: 'pointer' }}>Create Pricing Profile</button>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: '1rem' }}>
        <h3>Item Pricing</h3>
        <div style={{ marginBottom: '0.7rem' }}>
          <select value={selectedPricingProfileId} onChange={(e) => setSelectedPricingProfileId(e.target.value)} style={{ ...inputStyle, minWidth: '320px' }}>
            <option value="">Select Pricing Profile</option>
            {pricingProfiles.map((p) => <option key={p.id} value={p.id}>{p.name}{p.is_default ? ' (Default)' : ''}</option>)}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '0.7rem' }}>
          <select value={itemPricingForm.menu_item_id} onChange={(e) => setItemPricingForm({ ...itemPricingForm, menu_item_id: e.target.value })} style={inputStyle}>
            <option value="">Select Menu Item</option>
            {menuItems.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <input type="time" value={itemPricingForm.start_time} onChange={(e) => setItemPricingForm({ ...itemPricingForm, start_time: e.target.value })} style={inputStyle} />
          <input type="time" value={itemPricingForm.end_time} onChange={(e) => setItemPricingForm({ ...itemPricingForm, end_time: e.target.value })} style={inputStyle} />
          <input type="number" step="0.01" placeholder="Price" value={itemPricingForm.price} onChange={(e) => setItemPricingForm({ ...itemPricingForm, price: e.target.value })} style={inputStyle} />
          <label><input type="checkbox" checked={itemPricingForm.is_active} onChange={(e) => setItemPricingForm({ ...itemPricingForm, is_active: e.target.checked })} /> Active</label>
        </div>
        <div style={{ marginTop: '0.75rem' }}>
          <button onClick={addItemPricing} className="btn-primary" style={{ border: 'none', borderRadius: '6px', padding: '0.6rem 1rem', cursor: 'pointer' }}>Add Item Pricing</button>
        </div>
      </div>

      <div className="filter-bar">
        <h3>Pricing Rows</h3>
        {itemPricingRows.length === 0 ? <p>No pricing rows for selected profile</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Item</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Start</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>End</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Price</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Active</th>
              </tr>
            </thead>
            <tbody>
              {itemPricingRows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                  <td style={{ padding: '0.6rem' }}>{r.menu_item_name || r.menu_item_id}</td>
                  <td style={{ padding: '0.6rem' }}>{String(r.start_time || '').slice(0, 5) || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{String(r.end_time || '').slice(0, 5) || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{Number(r.price || 0).toFixed(2)}</td>
                  <td style={{ padding: '0.6rem' }}>{r.is_active ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default KotPricingProfileMaster;
